/**
 * Quota limits and calculation for billing tiers
 */
/**
 * Tier quota definitions by plan.
 *
 * Every number here is a product decision with no recorded rationale: the
 * table was written into task 57's spec as it stands, and earlier specs carried
 * different placeholder values. No cost model or measurement sits behind it.
 * The marketing site's pricing and the onboarding tier cards read their limits
 * from here (task 278), and `apps/landing/tests/expected-plans.ts` is compared
 * with it, so changing a number fails that table until a human re-reads what
 * the site will publish. Prices are not here: what a plan costs is its Stripe
 * price, and a copy of it in this table was read by nothing (task 278 removed
 * it). See `docs/guides/constant-provenance.md`.
 */
export const TIER_QUOTAS = {
    free: {
        monthlyReportLimit: 10_000,
        softLimitMultiplier: 3,
        siteLimit: 3,
        retentionDays: 15,
        userLimit: 1,
    },
    team: {
        monthlyReportLimit: 100_000,
        softLimitMultiplier: 3,
        siteLimit: 10,
        retentionDays: 30,
        userLimit: 10,
    },
    professional: {
        monthlyReportLimit: 250_000,
        softLimitMultiplier: 3,
        siteLimit: 25,
        retentionDays: 60,
        userLimit: 50,
    },
    business: {
        monthlyReportLimit: 750_000,
        softLimitMultiplier: 3,
        siteLimit: 100,
        retentionDays: 60,
        userLimit: 100,
    },
    // Not a published allowance: the floor a contract starts from (task 345).
    // Business's numbers, because an Enterprise tenant with no overrides is a
    // Business-sized tenant on Enterprise terms — every capability, and limits
    // that are data an operator sets rather than a deploy. The marketing site
    // never prints these figures (`PUBLISHED_PLANS` leaves Enterprise out), so
    // nothing states them as what the plan allows.
    enterprise: {
        monthlyReportLimit: 750_000,
        softLimitMultiplier: 3,
        siteLimit: 100,
        retentionDays: 60,
        userLimit: 100,
    },
};
/**
 * Length of the Team plan's free trial, in days. `checkout.ts` passes it to
 * Stripe as `trial_period_days`, and every page that states the trial reads it
 * from here rather than writing the number (task 278).
 */
export const TEAM_TRIAL_DAYS = 30;
/**
 * Days after which the Tinybird `ENGINE_TTL`s and the reports bucket's
 * `ExpireRawReports` rule remove a row whatever any tenant is entitled to.
 *
 * It is a backstop, not retention: the retention worker is what deletes a
 * tenant's data on its plan's schedule. `infra/test/retention-backstops.test.ts`
 * reads the datafiles and the synthesized bucket and fails when either
 * disagrees with this number.
 */
export const RETENTION_BACKSTOP_DAYS = 90;
/**
 * The longest retention a per-tenant override may set (task 345).
 *
 * Strictly below the backstop, because the retention worker runs once a day: a
 * tenant promised exactly 90 days would have rows removed by ClickHouse's TTL
 * partway through the day the worker was going to keep them, and nothing
 * anywhere would error. Raising it is a Tinybird and S3 change first, then this
 * number.
 */
export const MAX_RETENTION_DAYS = RETENTION_BACKSTOP_DAYS - 1;
/**
 * The most sites any tenant may hold: every plan's `siteLimit`, and every
 * per-tenant override, is bounded by it, and so is how many site ids one
 * request may filter by (tasks 277, 345).
 *
 * It was `max(TIER_QUOTAS[*].siteLimit)` until task 345, which is exactly the
 * shape an override defeats — a tenant allowed 150 sites could not have
 * filtered by more than the largest plan's 100. A ceiling above every plan
 * instead, so the filter bound is the same number whether a tenant's allowance
 * comes from its plan or its contract.
 *
 * **What sets the number is the request line, not a product decision.**
 * `GET /issues?siteId=…` carries the ids as a comma-separated query parameter,
 * and each one costs 39 bytes (36 for the UUID, 3 for an encoded comma).
 * API Gateway allows about 10 KB for the request line and headers together,
 * with the Descope JWT taking a kilobyte of it: 500 was tried first and the
 * sandbox answered `414 URI Too Long` before the Lambda ran. 150 is about
 * 5.9 KB of ids, which leaves room for the token and the other parameters.
 * Raising it means moving the filter off the query string first.
 *
 * A list this long still outgrows Tinybird's own URL, so `pipeRequest` sends a
 * long parameter set as a form-encoded POST body, which Tinybird accepts for
 * any endpoint.
 */
export const MAX_SITE_LIMIT = 150;
/**
 * The ceiling each override is bounded by.
 *
 * Two of them exist because something downstream silently stops working above
 * them — `retentionDays` past ClickHouse's TTL, `siteLimit` past what a filter
 * and an ownership query are sized for. The other two are sanity bounds: the
 * columns are Postgres `integer`, and a limit of a billion reports a month is a
 * typo rather than a contract.
 */
export const TENANT_LIMIT_CEILINGS = {
    monthlyReportLimit: 100_000_000,
    siteLimit: MAX_SITE_LIMIT,
    userLimit: 10_000,
    retentionDays: MAX_RETENTION_DAYS,
};
/**
 * A tenant's actual limits: its plan's figures with its own overrides on top.
 *
 * This is what every limit decision reads. `getQuotaForPlan` below is the
 * plan's *published* figures, and only the surfaces that describe plans rather
 * than tenants — the pricing page, the onboarding cards — may use it.
 *
 * Overrides are clamped to `TENANT_LIMIT_CEILINGS` here rather than trusted
 * from the row. The route that writes them refuses anything higher, so a
 * stored value above a ceiling means somebody wrote SQL; clamping keeps that
 * from reaching a backstop that would truncate data without saying so. The
 * floor — an override below the plan's own figure — is deliberately *not*
 * applied here: that is a downgrade, which is a billing decision the write
 * route refuses, not a fact about what the system can do.
 */
export function resolveQuota(plan, overrides) {
    const base = TIER_QUOTAS[plan];
    if (!overrides)
        return base;
    const apply = (field, planValue) => {
        const value = overrides[field];
        if (value === null || !Number.isFinite(value))
            return planValue;
        return Math.min(value, TENANT_LIMIT_CEILINGS[field]);
    };
    return {
        ...base,
        monthlyReportLimit: apply("monthlyReportLimit", base.monthlyReportLimit),
        siteLimit: apply("siteLimit", base.siteLimit),
        userLimit: apply("userLimit", base.userLimit),
        retentionDays: apply("retentionDays", base.retentionDays),
    };
}
/** Whether a tenant has any limit of its own. */
export function hasLimitOverrides(overrides) {
    return (overrides !== null &&
        overrides !== undefined &&
        Object.values(overrides).some((value) => value !== null));
}
/**
 * A plan's published limits.
 *
 * Only for surfaces that describe a *plan*: the pricing page, the onboarding
 * tier cards, the plans table in the billing guide. Anything deciding what a
 * *tenant* may do reads `resolveQuota` instead, so a contract is never quietly
 * replaced by the plan's floor.
 */
export function getQuotaForPlan(plan) {
    return TIER_QUOTAS[plan];
}
/**
 * Calculate the hard limit (soft limit * multiplier)
 */
export function getHardLimit(quota) {
    return quota.monthlyReportLimit * quota.softLimitMultiplier;
}
/**
 * Determine quota status based on current usage
 */
export function getQuotaStatus(current, quota) {
    if (current < quota.monthlyReportLimit) {
        return "under";
    }
    const hardLimit = getHardLimit(quota);
    if (current >= hardLimit) {
        return "hard_exceeded";
    }
    return "soft_exceeded";
}
/**
 * Share of the monthly limit used, as a whole percentage (0-100+).
 *
 * Floored, not rounded, so the figure a customer sees crosses a threshold at
 * the same report the status does: 79.5% reads as 79, never as an "80%" that
 * is still `under` the 80% warning, and 99.5% reads as 99 rather than a "100%"
 * that is not yet over the limit (task 278).
 */
export function getPercentUsed(current, quota) {
    // Multiplied before dividing: `(2_900 / 10_000) * 100` is 28.999…, which
    // floors to 28, while an integer over the limit is exact enough to floor.
    return Math.floor((current * 100) / quota.monthlyReportLimit);
}
/**
 * Calculate billing period boundaries
 *
 * For tenants with Stripe subscriptions, uses currentPeriodStart from subscription.
 * For free tier, uses calendar month (first day of current month, 00:00:00 UTC).
 */
export function calculatePeriodBounds(subscription) {
    if (subscription) {
        return {
            start: new Date(subscription.currentPeriodStart),
            end: new Date(subscription.currentPeriodEnd),
        };
    }
    // Free tier: use calendar month
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
    return { start, end };
}
//# sourceMappingURL=limits.js.map